// SPDX-License-Identifier: AGPL-3.0-or-later

import {MACOS_INPUT_MONITORING_PERMISSION_NAME, PRODUCT_NAME} from '@app/features/app/config/I18nDisplayConstants';
import KeybindManager from '@app/features/app/keybindings/KeybindManager';
import {Button} from '@app/features/ui/button/Button';
import {WarningAlert} from '@app/features/ui/warning_alert/WarningAlert';
import * as InputMonitoringPermissionCommands from '@app/features/voice/commands/InputMonitoringPermissionCommands';
import NativePermission from '@app/features/voice/state/NativePermission';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useState} from 'react';

const SYSTEM_WIDE_SHORTCUTS_DESCRIPTOR = msg({
	message: 'System-wide shortcuts',
	comment: 'Settings subsection title for keyboard and mouse shortcuts that work outside the app.',
});
const MACOS_DESCRIPTION_DESCRIPTOR = msg({
	message:
		'{macosInputMonitoringPermissionName} is required for system-wide keyboard and mouse shortcuts on macOS. After changing the permission, fully quit and restart {productName} so shortcuts can use it.',
	comment:
		'Settings warning for macOS system-wide shortcuts. {macosInputMonitoringPermissionName} is the macOS permission name and {productName} is the app name.',
});
const WAYLAND_DESCRIPTION_DESCRIPTOR = msg({
	message:
		'Input access is required for system-wide keyboard and mouse shortcuts on Wayland. After changing input access, fully quit and restart {productName} so shortcuts can use it.',
	comment: 'Settings warning for Linux Wayland system-wide shortcuts. {productName} is the app name.',
});
const REVIEW_PERMISSION_DESCRIPTOR = msg({
	message: 'Review permission',
	comment: 'Button label in the keybind settings system-wide shortcuts section.',
});
const ENABLE_DESCRIPTOR = msg({
	message: 'Enable',
	comment: 'Button label in the keybind settings system-wide shortcuts section.',
});
const RECHECK_DESCRIPTOR = msg({
	message: 'Recheck',
	comment: 'Button label in the keybind settings system-wide shortcuts section.',
});
const FINISH_RELOGIN_DESCRIPTOR = msg({
	message: 'Input access was changed. Fully quit and restart {productName} so system-wide shortcuts can use it.',
	comment: 'Status text after Linux input access was changed. {productName} is the app name.',
});
const LINUX_ERROR_DESCRIPTOR = msg({
	message:
		'Input access could not be enabled automatically. Change it manually, then fully quit and restart {productName} so shortcuts can use it.',
	comment: 'Status text after Linux input access setup failed. {productName} is the app name.',
});
export const InputMonitoringSection: React.FC = observer(() => {
	const {i18n} = useLingui();
	const [busy, setBusy] = useState(false);
	const isMacOS = NativePermission.isNativeMacDesktop;
	const isLinuxWayland = NativePermission.isLinuxWaylandDesktop;
	if (!isMacOS && !isLinuxWayland) return null;
	const macStatus = NativePermission.inputMonitoringStatus;
	const linuxStatus = NativePermission.linuxInputAccessStatus;
	const granted = isMacOS ? macStatus === 'granted' : linuxStatus === 'granted';
	if (granted) return null;
	const description = isMacOS
		? i18n._(MACOS_DESCRIPTION_DESCRIPTOR, {
				macosInputMonitoringPermissionName: MACOS_INPUT_MONITORING_PERMISSION_NAME,
				productName: PRODUCT_NAME,
			})
		: i18n._(WAYLAND_DESCRIPTION_DESCRIPTOR, {productName: PRODUCT_NAME});
	const body = (() => {
		if (!isLinuxWayland) return null;
		if (NativePermission.linuxInputAccessGrantNeedsRelogin) {
			return i18n._(FINISH_RELOGIN_DESCRIPTOR, {productName: PRODUCT_NAME});
		}
		if (NativePermission.linuxInputAccessGrantError) return i18n._(LINUX_ERROR_DESCRIPTOR, {productName: PRODUCT_NAME});
		return null;
	})();
	const buttonLabel = (() => {
		if (isMacOS) return i18n._(REVIEW_PERMISSION_DESCRIPTOR);
		if (NativePermission.linuxInputAccessGrantNeedsRelogin) return i18n._(RECHECK_DESCRIPTOR);
		return i18n._(ENABLE_DESCRIPTOR);
	})();
	const handleMacReview = () => {
		InputMonitoringPermissionCommands.openInputMonitoringPermissionModal({
			onComplete: () => {
				void NativePermission.recheckInputMonitoring().then(() => KeybindManager.reapplyGlobalShortcuts());
			},
		});
	};
	const handleLinuxAction = async () => {
		setBusy(true);
		try {
			if (granted || NativePermission.linuxInputAccessGrantNeedsRelogin) {
				await NativePermission.recheckLinuxInputAccess();
			} else {
				await NativePermission.grantLinuxInputAccess();
			}
			await KeybindManager.reapplyGlobalShortcuts();
		} finally {
			setBusy(false);
		}
	};
	return (
		<WarningAlert
			title={i18n._(SYSTEM_WIDE_SHORTCUTS_DESCRIPTOR)}
			actions={
				isMacOS ? (
					<Button
						variant="primary"
						small={true}
						onClick={handleMacReview}
						data-flx="user.input-monitoring-section.button.review"
					>
						{buttonLabel}
					</Button>
				) : (
					<Button
						variant="primary"
						small={true}
						onClick={handleLinuxAction}
						submitting={busy}
						data-flx="user.input-monitoring-section.button.enable-linux"
					>
						{buttonLabel}
					</Button>
				)
			}
			data-flx="user.input-monitoring-section.settings-tab-section"
		>
			{body ?? description}
		</WarningAlert>
	);
});
